import { InputTime, InputDate, InputNumber } from "@sellar-io/components";
import { daysOfWeek } from "@sellar-io/common";
import dayjs from "dayjs";
import React from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Alert } from "../../../../components/alert/Alert";
import { ReactHookFormSwitch } from "../../../../components/form/ReactHookFormSwitch";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { deliveryDays } from "../../../deliveryAreas/DeliveryAreaForm.shared";
import { DeliveryDaysInput } from "../../../deliveryAreas/DeliveryDaysInput";
import { CourierDeliveryScheduleFormType } from "../../modals/CourierScheduleCreateDialog";
import { CourierDeliveryScheduleActionPanel } from "../CourierDeliveryScheduleActionPanel";

export const CourierScheduleForm = ({
  form,
  onSubmit,
  selectedDays,
  setSelectedDays,
  showWarning,
  formId,
}: {
  form: UseFormReturn<CourierDeliveryScheduleFormType>;
  onSubmit: (data: CourierDeliveryScheduleFormType) => void;
  selectedDays: daysOfWeek[];
  setSelectedDays: (selectedDays: daysOfWeek[]) => void;
  showWarning: boolean;
  formId?: string;
}) => {
  const orderCutOffTime = useWatch({
    control: form.control,
    name: "orderCutOffTime",
  });
  const dispatchDaysAfterConfirmation = useWatch({
    control: form.control,
    name: "dispatchDaysAfterConfirmation",
  });
  const deliveryDaysAfterDispatch = useWatch({
    control: form.control,
    name: "deliveryDaysAfterDispatch",
  });
  const startAt = useWatch({
    control: form.control,
    name: "startAt",
  });
  const allowSaturdayDelivery = useWatch({
    control: form.control,
    name: "allowSaturdayDelivery",
  });
  return (
    <>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className={"flex flex-col gap-2"}
      >
        <DeliveryDaysInput
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          label={"Select which day(s) you ship on"}
          showWarning={showWarning}
          deliveryDays={deliveryDays.filter(
            (day) => !["sunday", "saturday"].includes(day.value),
          )}
        />
        <InputLabel>When finalising an order before...</InputLabel>
        <InputContainer>
          <InputTime
            {...form.register("orderCutOffTime", {
              required: true,
            })}
          />
        </InputContainer>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-1">
            <InputLabel>Pre-fill the estimate DISPATCH date to be</InputLabel>
            <InputContainer>
              <InputNumber
                {...form.register("dispatchDaysAfterConfirmation", {
                  required: true,
                })}
              />
            </InputContainer>
          </div>
          <div className="col-span-1 flex items-end">
            Working day(s) after finalisation
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-1">
            <InputLabel>Pre-fill the estimate DELIVERY date to be</InputLabel>
            <InputContainer>
              <InputNumber
                {...form.register("deliveryDaysAfterDispatch", {
                  required: true,
                })}
              />
            </InputContainer>
          </div>
          <div className="col-span-1 flex items-end">
            Working day(s) after dispatch
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 flex flex-col gap-1">
            <InputLabel>
              Show customers that you offer Saturday delivery if dispatching on
              Friday
            </InputLabel>
            <div className="flex items-center gap-2">
              <ReactHookFormSwitch
                control={form.control}
                name="allowSaturdayDelivery"
              />{" "}
              <span>{allowSaturdayDelivery ? "On" : "Off"}</span>
            </div>
          </div>
        </div>
        <div className="w-full">
          <InputLabel>Starting on</InputLabel>
          <InputContainer>
            <InputDate
              className={"w-full"}
              {...form.register("startAt", {
                required: "Return date must be provided",
              })}
              min={dayjs().format("YYYY-MM-DD")}
            />
          </InputContainer>
        </div>
      </form>

      <CourierDeliveryScheduleActionPanel
        className={"mt-2"}
        orderCutOffTime={orderCutOffTime}
        dispatchDaysAfterConfirmation={dispatchDaysAfterConfirmation}
        deliveryDaysAfterDispatch={deliveryDaysAfterDispatch}
        selectedDays={selectedDays}
        startAt={dayjs(startAt).toDate()}
        allowSaturdayDelivery={allowSaturdayDelivery}
      />
      {!!selectedDays.length && (
        <Alert
          className={"mt-4"}
          message={
            <p>
              <strong>Please note:</strong> Your updated courier delivery
              schedule will be shown live on your storefront to your customers
              when you click ‘Confirm’
            </p>
          }
          type={"information"}
        />
      )}
    </>
  );
};
